/**
 * Investor deck content — text from "Ageon Investor Opportunity" PDF.
 * pdfPage = source slide for each block.
 */
import {
    Activity, AlertCircle, Brain, Dna, Flame, HeartPulse, Microscope,
    ScanSearch, Shield, Sparkles, Target, TrendingUp, Users, Wind, Zap,
} from 'lucide-react';

export const INVESTOR_INTRODUCTION = {
    id: 'ageon-introduction',
    title: 'AGEON Introduction',
    pdfPages: [2, 3, 5, 6, 7],
    problem: {
        pdfPage: 2,
        eyebrow: 'The core problem',
        heading: 'India is living longer. But not healthier.',
        body: 'Modern life is increasing stress, fatigue, metabolic dysfunction, poor recovery, hormonal imbalance, and accelerated ageing. Most people act only after symptoms appear.',
        tags: ['Stress & burnout', 'Metabolic dysfunction', 'Poor recovery', 'Reactive healthcare'],
    },
    shift: {
        pdfPage: 3,
        eyebrow: 'Category shift',
        heading: 'The longevity category has arrived in India',
        body: 'Prevention, recovery and smart-ageing are moving from niche consumption to premium mainstream demand.',
        pillars: [
            { title: 'Biomarker-led wellness', icon: ScanSearch },
            { title: 'Recovery & regeneration', icon: HeartPulse },
            { title: 'Personalized memberships', icon: Users },
            { title: 'Preventive care', icon: Shield },
        ],
    },
    competitor: {
        pdfPage: 5,
        eyebrow: 'Competitor reality',
        heading: 'The category is active. The white space is systemization.',
        body: 'AIWO, Decode Age, BioPeak, and premium preventive players are educating the market, but the category remains fragmented.',
        highlight: 'AGEON opportunity: integrated, premium, protocol-led, measurable and scalable.',
    },
    system: {
        pdfPage: 6,
        eyebrow: 'What AGEON is',
        heading: 'Not a clinic. Not a spa. A smart-ageing system.',
        features: [
            { title: 'Advanced diagnostics', desc: 'Long-term health intelligence', icon: Microscope },
            { title: 'AGEON Score', desc: 'Baseline & health dashboard', icon: Target },
            { title: 'Protocol-led therapies', desc: 'Vitality & recovery', icon: Activity },
            { title: 'Progress tracking', desc: 'Measurable outcomes', icon: TrendingUp },
            { title: 'Premium experience', desc: 'Member wellness journey', icon: Sparkles },
        ],
    },
    difference: {
        pdfPage: 7,
        eyebrow: 'The AGEON difference',
        heading: 'Machines are not the moat. Protocols are.',
        body: 'AGEON moves from fragmented sessions to structured smart-ageing journeys — assessment-led entry, protocol-led therapy selection, guided delivery, tracked outcomes, and recurring memberships.',
    },
};

export const INVESTOR_AGEON_SCORE = {
    id: 'ageon-score',
    title: 'AGEON Score',
    pdfPages: [9, 10],
    intro: {
        pdfPage: 9,
        eyebrow: 'Proprietary intelligence',
        heading: 'Introducing AGEON Score',
        body: 'A healthspan dashboard that turns wellness from opinion into measurable progress — the measurement engine for premium memberships, renewals, and repeat protocols.',
    },
    pillars: [
        { name: 'Biological age', desc: 'How the body is ageing biologically', icon: Dna },
        { name: 'Metabolic health', desc: 'Glucose, lipids, body composition and energy efficiency', icon: Activity },
        { name: 'Recovery & inflammation', desc: 'Stress load, resilience and tissue recovery', icon: HeartPulse },
        { name: 'Energy, hormones & sleep', desc: 'Vitality, hormonal balance and repair', icon: Zap },
    ],
    metrics: {
        pdfPage: 10,
        eyebrow: 'What we measure',
        heading: 'One integrated health measurement system',
        body: 'Not services in isolation — a unified view of healthspan across biological, metabolic, cardiovascular, and recovery markers.',
        items: [
            'Biological age & cellular ageing score',
            'Metabolic health — glucose trends, body composition, energy efficiency',
            'Cardiovascular risk & functional screening',
            'Inflammation, recovery, stress load & tissue recovery',
            'Endurance baseline, resilience capacity & early warning indicators',
        ],
    },
};

export const INVESTOR_DIAGNOSTICS = {
    id: 'diagnostics-engine',
    title: 'Diagnostics Engine',
    pdfPages: [11],
    intro: {
        pdfPage: 11,
        heading: 'The AGEON Diagnostics Engine',
        body: 'A premium assessment layer that turns wellness into measurable baseline intelligence. Diagnostics establish baseline, identify risk, and personalize protocol — the entry funnel to recurring memberships and high-value protocols.',
    },
    panels: [
        { name: 'Blood biomarkers', desc: 'Inflammation, hormone health, organ function and longevity markers.', icon: Microscope },
        { name: 'Advanced cardiac screening', desc: 'ECG, HRV and cardiac risk assessment for early detection.', icon: HeartPulse },
        { name: 'Genomic testing', desc: 'Genetic insights for personalized wellness planning.', icon: Dna },
        { name: 'Brain & cognition testing', desc: 'Cognitive function, memory and mental resilience.', icon: Brain },
        { name: 'Body composition analysis', desc: 'DEXA & InBody scans for lean mass, bone density and visceral health.', icon: ScanSearch },
        { name: 'VO₂ Max / RMR testing', desc: 'Aerobic capacity and resting metabolic rate for longevity.', icon: Wind },
        { name: 'Gut microbiome analysis', desc: 'Digestive, immune and metabolic drivers uncovered.', icon: Activity },
    ],
};

export const INVESTOR_THERAPIES = {
    id: 'therapies-to-protocol',
    title: 'Therapies to Protocol',
    pdfPages: [13],
    intro: {
        pdfPage: 13,
        heading: 'From therapies to protocols',
        body: 'AGEON does not sell random wellness sessions. It delivers structured, evidence-led protocols with measurable outcomes — designed for repeat usage, franchise scalability, and a premium member experience.',
    },
    programs: [
        { name: 'Fat loss & metabolic reset', desc: 'Improves body composition, insulin sensitivity and metabolic efficiency.', icon: Flame },
        { name: 'Skin vitality & aesthetic wellness', desc: 'Supports skin health, tone, glow and appearance from within.', icon: Sparkles },
        { name: 'Stress & nervous system reset', desc: 'Reduces overload, improves calm and emotional balance.', icon: Wind },
        { name: 'Recovery & performance', desc: 'Supports stamina, resilience and physical recovery.', icon: Activity },
        { name: 'Sleep, energy & hormonal support', desc: 'Supports rest, vitality and internal balance.', icon: Zap },
        { name: 'Longevity & healthy ageing', desc: 'Improves recovery, vitality and long-term functional health.', icon: Target },
    ],
};
